import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Quote } from "lucide-react";
import { useSiteData } from "../context/SiteDataContext";
import { getSection, imageStyle, renderMultilineText, sectionStyle } from "../lib/sectionStyles";
import SafeImage from "../components/SafeImage";

const FeaturedCover = () => {
  const { featuredCover, siteSections } = useSiteData();
  const section = getSection(siteSections, "home", "home.featured-cover", {
    eyebrow: "Featured",
    label: featuredCover.kicker,
    title: featuredCover.title,
    subtitle: featuredCover.category,
    body: featuredCover.dek,
    caption: featuredCover.pullQuote,
    imageUrl: featuredCover.image,
    imageAlt: featuredCover.title,
    imagePosition: featuredCover.imagePosition || "top",
    imageFit: featuredCover.imageFit || "cover",
    ctaLabel: featuredCover.externalUrl ? "READ THE COVER STORY" : "BROWSE THE PORTFOLIO",
    ctaUrl: featuredCover.externalUrl || "/journalist-portfolio",
    content: {
      publication: featuredCover.publication,
      category: featuredCover.category,
      date: featuredCover.date,
      stats: featuredCover.stats,
    },
  });

  if (!section) return null;

  const stats = Array.isArray(section.content?.stats) ? section.content.stats : [];
  const ctaUrl = section.ctaUrl || "/journalist-portfolio";
  const ctaLabel = section.ctaLabel || (ctaUrl.startsWith("http") ? "READ THE COVER STORY" : "BROWSE THE PORTFOLIO");
  const publication = section.content?.publication || featuredCover.publication || "";

  return (
    <section id="stories" className="spread-accent border-y-2 border-[var(--ink)] mt-10" style={sectionStyle(section)}>
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-14 md:py-20">
        <div className="flex items-center gap-3 mb-6">
          <span className="h-1 w-12 bg-[var(--pink)]" />
          <span className="stamp">{section.eyebrow || "FEATURED"}</span>
          <span className="h-1 w-6 bg-[var(--pink)]" />
          <span className="font-mono text-[11px] tracking-[0.3em] uppercase text-[var(--ink-soft)]">
            {section.label}
          </span>
        </div>

        <div className="grid grid-cols-12 gap-6 md:gap-10 items-start">
          <div className="col-span-12 md:col-span-7 relative">
            <div className="relative border-4 border-[var(--ink)] overflow-hidden bg-[var(--ink)] aspect-[5/6] md:aspect-[4/5]">
              <SafeImage
                src={section.imageUrl || featuredCover.image}
                fallbackSrc={featuredCover.image}
                alt={section.imageAlt || section.title}
                loading="lazy"
                width="940"
                height="1175"
                className="w-full h-full object-cover object-top"
                style={imageStyle(section)}
              />
              <div className="absolute top-3 left-3 bg-[var(--paper)] border-2 border-[var(--ink)] px-2 py-1 font-mono text-[10px] tracking-[0.25em]">
                COVER No. 01
              </div>
              <div className="absolute bottom-3 right-3 bg-[var(--red)] text-[var(--pink-soft)] px-3 py-1 font-masthead tracking-[0.22em] text-xs">
                AS PUBLISHED IN {publication.toUpperCase()}
              </div>
            </div>
            <div className="mt-3 font-mono text-[11px] uppercase tracking-[0.25em] text-[var(--ink-soft)]">
              Spread - {section.content?.date || featuredCover.date || "2025"}
            </div>
          </div>

          <div className="col-span-12 md:col-span-5">
            <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[var(--red)] mb-3">
              {section.subtitle || section.content?.category}
            </p>
            <h3 className="font-editorial text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight">
              {renderMultilineText(section.title)}
            </h3>
            <p className="mt-5 text-lg leading-relaxed text-[var(--ink-soft)]">
              {renderMultilineText(section.body)}
            </p>

            {section.caption ? (
              <div className="mt-6 border-l-4 border-[var(--red)] pl-4 py-2 bg-[var(--paper-dark)]">
                <Quote size={16} className="text-[var(--red)] mb-1" />
                <p className="font-editorial italic text-xl leading-snug">
                  {renderMultilineText(section.caption)}
                </p>
              </div>
            ) : null}

            {stats.length > 0 && (
              <div className="grid grid-cols-3 gap-3 mt-6">
                {stats.map((s) => (
                  <div key={s.label} className="border-2 border-[var(--ink)] p-3 bg-[var(--paper)]">
                    <div className="font-masthead text-3xl text-[var(--red)] leading-none">
                      {s.value}
                    </div>
                    <div className="font-mono text-[10px] tracking-[0.25em] uppercase mt-1">
                      {s.label}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {ctaUrl.startsWith("http") || ctaUrl.startsWith("mailto:") ? (
              <a
                href={ctaUrl}
                target={ctaUrl.startsWith("http") ? "_blank" : undefined}
                rel={ctaUrl.startsWith("http") ? "noopener noreferrer" : undefined}
                className="mt-7 inline-flex items-center gap-2 font-masthead tracking-[0.22em] text-sm border-2 border-[var(--ink)] px-5 py-3 bg-[var(--paper)] hover:bg-[var(--ink)] hover:text-[var(--paper)] transition-colors"
              >
                {ctaLabel}
                <ArrowUpRight size={16} />
              </a>
            ) : (
              <Link
                to={ctaUrl}
                className="mt-7 inline-flex items-center gap-2 font-masthead tracking-[0.22em] text-sm border-2 border-[var(--ink)] px-5 py-3 bg-[var(--paper)] hover:bg-[var(--ink)] hover:text-[var(--paper)] transition-colors"
              >
                {ctaLabel}
                <ArrowUpRight size={16} />
              </Link>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeaturedCover;
